import React from 'react';
import { Space, Tag, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../context/ThemeContext';
import Table from '../../commonComponents/Table';
import TablePagination from '../../commonComponents/Pagination';
import { MerchantsTableProps, Merchant } from './types';
import { ColumnsType } from 'antd/es/table';
import { ActionIconButton } from './styles';

const MerchantsTable: React.FC<MerchantsTableProps> = ({
  merchants,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();

  const columns: ColumnsType<Merchant> = [
    {
      title: 'USER',
      key: 'user',
      fixed: 'left',
      width: 120,
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.name || record.username || record.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'USERNAME',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (username) => (
        <Tag color="blue" style={{ borderRadius: '12px'}}>
          {username || '-'}
        </Tag>
      ),
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || '-',
    },
    {
      title: 'PHONE',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || '-',
    },
    {
      title: 'ADDRESS',
      dataIndex: 'address',
      key: 'address',
      render: (address) => address || '-',
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <ActionIconButton
            onClick={() => onEdit(record)}
            iconColor="#2D6CCA"
          >
            <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
          <ActionIconButton
            onClick={() => onDelete(record)}
            iconColor="#f5222d"
          >
            <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
        </Space>
      ),
    },
  ];

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      onPageSizeChange(size);
    }
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  return (
    <>
      <Table<Merchant>
        columns={columns}
        dataSource={merchants}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={merchants.length > 0 ? { x: true, y: 600 } : undefined}
        disableScroll={merchants.length === 0}
        emptyText="No merchants found"
        emptyDescription="No merchants found"
        headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
        removeVerticalBorders={true}
        thPadding="15px 10px"
        tdPadding="10px 10px"
        firstChildBorderRadius="8px"
        border="0"
        borderBottomLeftRight={true}
        theme={isDarkMode ? 'dark' : 'light'}
      />
      {merchants.length > 0 && (
        <TablePagination
          totalCount={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total records"
          padding="16px 12px 0px 12px"
        />
      )}
    </>
  );
};

export default MerchantsTable;